import { rsaEncrypt, rsaDecrypt, aesEncrypt, aesDecrypt } from './crypto';
import CryptoJS from 'crypto-js';

/**
 * Encrypt note content - using AES+RSA hybrid encryption
 * @param {string} content - Content to encrypt
 * @param {string} userPublicKey - User public key
 * @returns {Object} Object containing encrypted content and encrypted key
 */
export const encryptNoteContent = (content, userPublicKey) => {
  try {
    // 1. Generate random AES key (32 character string)
    const aesKey = Math.random().toString(36).substring(2, 34).padEnd(32, '0');
    
    // 2. Use AES to encrypt content
    const aesEncrypted = aesEncrypt(content, aesKey);
    if (!aesEncrypted) {
      throw new Error('AES encryption failed');
    }

    // 3. Use user public key to encrypt AES key
    const encryptedAesKey = rsaEncrypt(aesKey, userPublicKey);
    if (!encryptedAesKey) {
      throw new Error('Failed to encrypt AES key with user public key');
    }

    return {
      encryptedContent: aesEncrypted,
      encryptedKey: encryptedAesKey
    };
  } catch (error) {
    throw new Error(`Failed to encrypt note content: ${error.message}`);
  }
};

/**
 * Decrypt note content - using AES+RSA hybrid decryption
 * @param {string} encryptedContent - AES encrypted content
 * @param {string} encryptedKey - RSA encrypted AES key
 * @param {string} userPrivateKey - User private key
 * @returns {string} Decrypted content
 */
export const decryptNoteContent = (encryptedContent, encryptedKey, userPrivateKey) => {
  try {
    // 1. Use user private key to decrypt AES key
    const aesKey = rsaDecrypt(encryptedKey, userPrivateKey);
    if (!aesKey) {
      throw new Error('Failed to decrypt AES key with user private key');
    }

    // 2. Use AES key to decrypt content
    const decryptedContent = aesDecrypt(encryptedContent, aesKey);
    if (!decryptedContent) {
      throw new Error('AES decryption failed');
    }

    return decryptedContent;
  } catch (error) {
    throw new Error(`Failed to decrypt note content: ${error.message}`);
  }
};

/**
 * Encrypt note title and content
 * @param {string} title - Note title
 * @param {string} content - Note content
 * @param {string} userPublicKey - User public key
 * @returns {Object} Object containing encrypted title, content and key
 */
export const encryptNote = (title, content, userPublicKey) => {
  try {
    // 1. Generate a shared AES key (32 character string, ensure AES function can handle it correctly)
    const aesKey = Math.random().toString(36).substring(2, 34).padEnd(32, '0');
    
    // 2. Use the same AES key to encrypt title and content
    const titleEncrypted = aesEncrypt(title, aesKey);
    const contentEncrypted = aesEncrypt(content, aesKey);
    
    if (!titleEncrypted || !contentEncrypted) {
      throw new Error('AES encryption failed');
    }

    // 3. Use user public key to encrypt AES key
    const noteKeyEncrypted = rsaEncrypt(aesKey, userPublicKey);
    if (!noteKeyEncrypted) {
      throw new Error('Failed to encrypt AES key with user public key');
    }

    return {
      titleEncrypted,
      contentEncrypted,
      noteKeyEncrypted,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Decrypt note title and content
 * @param {string} titleEncrypted - Encrypted title
 * @param {string} contentEncrypted - Encrypted content
 * @param {string} noteKeyEncrypted - Encrypted AES key
 * @param {string} userPrivateKey - User private key
 * @returns {Object} Object containing decrypted title and content
 */
export const decryptNote = (titleEncrypted, contentEncrypted, noteKeyEncrypted, userPrivateKey) => {
  try {
    // First decrypt the AES key
    const aesKey = rsaDecrypt(noteKeyEncrypted, userPrivateKey);
    if (!aesKey) {
      throw new Error('Failed to decrypt AES key');
    }

    // Use AES key to decrypt title and content
    const title = aesDecrypt(titleEncrypted, aesKey);
    const content = aesDecrypt(contentEncrypted, aesKey);

    if (!title || !content) {
      throw new Error('AES decryption failed');
    }

    return {
      title,
      content,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Validate if encrypted content is valid
 * @param {string} encryptedContent - Encrypted content
 * @returns {boolean} Whether it's valid
 */
export const isValidEncryptedContent = (encryptedContent) => {
  if (!encryptedContent || typeof encryptedContent !== 'string') {
    return false;
  }

  // Check if it contains Base64 characters
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  return base64Regex.test(encryptedContent) && encryptedContent.length > 0;
};

/**
 * Get content size in bytes
 * @param {string} content - Content
 * @returns {number} Size in bytes
 */
export const getContentSize = (content) => {
  if (!content) return 0;
  return new Blob([content]).size;
};

/**
 * Format file size
 * @param {number} bytes - Number of bytes
 * @returns {string} Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
